import { last } from 'lodash-es'
import { computed } from '@/composition/vue/compositionApi'
import { getTranslateService, getValidationService } from '@/core/container/ioc'
import type { ValidationRule, ValidationRuleError, ValidationServerError } from '@/services/validation/types'
import type { TranslationKey } from '@/services/types'

interface ErrorMessageFormattingParams {
  field: string
  errors: ValidationRuleError[]
  serverErrors?: ValidationServerError[]
  errorCustomMessage?: string
  invalid: boolean
}

export const getErrorMessagesComputed = (params: ErrorMessageFormattingParams) =>
  computed(() => getErrorMessages(params))

const errorToString = (field: string, error: ValidationRuleError | string) => {
  if (typeof error === 'string') {
    return error
  }
  const translateService = getTranslateService()
  const fieldName: string = last(field.split('.')) as string
  const translateKey = `fieldNames.${fieldName}` as TranslationKey
  let fieldNameText = fieldName
  if (translateService.exists(translateKey)) {
    fieldNameText = translateService.t(translateKey)
  }

  return translateService.translateValidationError(error, fieldNameText)
}

export const getErrorMessages = (params: ErrorMessageFormattingParams) => {
  if (params.invalid && params.errorCustomMessage) {
    return [params.errorCustomMessage]
  }
  if (!params.errors) {
    return []
  }
  const ruleErrors = params.errors.map((error) => errorToString(params.field, error))
  const serverErrors = (params.serverErrors || []).map((error) => errorToString(params.field, error))

  return [...ruleErrors, ...serverErrors]
}

export const getFieldErrors = (validators: ValidationRule[], errors: ValidationRuleError[]) => {
  const translateService = getTranslateService()
  const validationService = getValidationService()

  const hasErrorByRuleName = (ruleName: string) => errors.find((error) => error.ruleName === ruleName)

  const errorsMap = validators.reduce((map, rule) => {
    const validationError = validationService.getValidationMessage(rule)
    const message = translateService.translateValidationError(validationError)
    const isValid = map[message] === false ? false : !hasErrorByRuleName(rule.name)
    return {
      ...map,
      [message]: isValid,
    }
  }, {})

  return Object.entries(errorsMap).map(([message, isValid]) => ({ message, isValid }))
}
